/**
 * Function to determine the largest rectangle that can fit within a 2d area
 * @module
 */
import { aabb2Area } from "./aabb2";
import type { AABB2 } from "./aabb2Types";
import { aabb2Height, aabb2New, aabb2Width } from "./aabb2Types";
import { classifyContainmentVec2PolyLine2Fast } from "./containment";
import { ANGLE_EPSILON, DIST_EPSILON } from "./core";
import { line2IsAxisAligned } from "./line2";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2FromAABB2,
  polyLine2IsAxisAlignedRect,
  polyLine2Rotate,
} from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { ascendingSortCompare } from "./utils";
import { vec2AngleBetween, vec2AngleBetween as vec2AngleBetweenCW, vec2Sub } from "./vec2";

/** Normalization factor since we only need 90 degrees of rotation to get purely horizontal or vertical edges*/
const QUARTER_ROTATION_NORMALIZATION_ANGLE = Math.PI / 2;
const MAX_DIFFERENCE_TO_BE_DUPLICATE_NUMBER = 0.001;
export interface MaximalRectWithDims {
  readonly maxRectAsPolyLine2: PolyLine2;
  /** width fails to longest-edge-aligned aabb2 width if maxRect fails */
  readonly width: number;
  /** height fails to longest-edge-aligned aabb2 height if maxRect fails */
  readonly height: number;
  readonly aabbWidth: number;
  readonly aabbHeight: number;
  readonly aabbAsPolyLine2: PolyLine2;
  readonly maxRectFailed?: boolean;
  readonly isAlreadyRectangle?: boolean;
  readonly angleToVertical: number;
}

export function polyLine2MaximalRectangle(
  pl: PolyLine2,
  preComputedLongestEdgeAngle?: number,
  cellNumberLimit?: number,
): MaximalRectWithDims {
  //fast path
  if (polyLine2IsAxisAlignedRect(pl)) {
    const aabb2 = polyLine2AABB2(pl);
    return {
      maxRectAsPolyLine2: polyLine2Copy(pl),
      width: aabb2Width(aabb2),
      height: aabb2Height(aabb2),
      aabbAsPolyLine2: polyLine2FromAABB2(aabb2),
      isAlreadyRectangle: true,
      aabbWidth: aabb2Width(aabb2),
      aabbHeight: aabb2Height(aabb2),
      angleToVertical: 0,
    };
  }

  const [tx, ty] = polyLine2LongestEdgeTangent(pl);

  let angleToVertical =
    preComputedLongestEdgeAngle !== undefined
      ? preComputedLongestEdgeAngle
      : vec2AngleBetweenCW(tx, ty, 0, 1) % QUARTER_ROTATION_NORMALIZATION_ANGLE;
  const absoluteAngle = Math.abs(angleToVertical);
  const isRotated =
    absoluteAngle > ANGLE_EPSILON && QUARTER_ROTATION_NORMALIZATION_ANGLE - absoluteAngle > ANGLE_EPSILON;
  if (!isRotated) {
    angleToVertical = 0;
  }

  const rotatedPl = isRotated ? polyLine2Rotate(polyLine2Copy(pl), angleToVertical, 0, 0) : pl;

  // fast path for rotated rectangles
  if (polyLine2IsAxisAlignedRect(rotatedPl)) {
    const aabb2 = polyLine2AABB2(rotatedPl);
    const { width, height } = getWidthAndHeightForAABB2AndRotationAngle(angleToVertical, aabb2);

    return {
      maxRectAsPolyLine2: polyLine2Copy(pl),
      width,
      height,
      aabbAsPolyLine2: rotatedPl
        ? polyLine2Rotate(polyLine2FromAABB2(aabb2), -angleToVertical, 0, 0)
        : polyLine2FromAABB2(aabb2),
      isAlreadyRectangle: true,
      aabbWidth: width,
      aabbHeight: height,
      angleToVertical: 0,
    };
  }

  let maxRect: AABB2 | undefined;
  let arr: number[][] | undefined;
  const { x: uniqueXs, y: uniqueYs } = polyLine2UniqueXsAndYs(rotatedPl);
  if (cellNumberLimit === undefined || uniqueXs.length * uniqueYs.length < cellNumberLimit) {
    arr = polyLine2EncodeAsAreaArray(rotatedPl, uniqueXs, uniqueYs);

    if (arr.length && arr[0].length) {
      const {
        lowerLeftColumn: recMinX,
        upperRightRow: recMinY,
        upperRightColumn: recMaxX,
        lowerLeftRow: recMaxY,
      } = maxRectangleWithArea(arr);

      const minX = uniqueXs[recMinX];
      const minY = uniqueYs[recMinY];
      const maxX = uniqueXs[recMaxX + 1];
      const maxY = uniqueYs[recMaxY + 1];

      maxRect = aabb2New(minX, minY, maxX, maxY);
    }
  }

  const aabb2 = polyLine2AABB2(pl);
  const aabb2WithPotentialRotation = isRotated
    ? polyLine2Rotate(polyLine2FromAABB2(polyLine2AABB2(rotatedPl)), -angleToVertical, 0, 0)
    : polyLine2FromAABB2(aabb2);
  const aabb2WithRotationForAABBdims = isRotated ? polyLine2AABB2(rotatedPl) : aabb2;
  const { width: aabbWidth, height: aabbHeight } = getWidthAndHeightForAABB2AndRotationAngle(
    angleToVertical,
    aabb2WithRotationForAABBdims,
  );

  // if max rect fails, fallback to bounding box
  if (!maxRect || !arr || arr.length === 0 || aabb2Area(maxRect) < DIST_EPSILON) {
    const aabb2WithRotationForAABBdims = isRotated ? polyLine2AABB2(rotatedPl) : aabb2;
    const { width: aabbWidth, height: aabbHeight } = getWidthAndHeightForAABB2AndRotationAngle(
      angleToVertical,
      aabb2WithRotationForAABBdims,
    );

    return {
      maxRectAsPolyLine2: aabb2WithPotentialRotation,
      width: aabbWidth,
      height: aabbHeight,
      aabbAsPolyLine2: aabb2WithPotentialRotation,
      maxRectFailed: true,
      aabbWidth,
      aabbHeight,
      angleToVertical,
    };
  }

  const { width, height } = getWidthAndHeightForAABB2AndRotationAngle(angleToVertical, maxRect);
  const finalPl = polyLine2FromAABB2(maxRect);
  return {
    maxRectAsPolyLine2: polyLine2Rotate(finalPl, -angleToVertical, 0, 0),
    width,
    height,
    aabbAsPolyLine2: aabb2WithPotentialRotation,
    aabbWidth,
    aabbHeight,
    angleToVertical,
  };
}

export function getWidthAndHeightForAABB2AndRotationAngle(angleToVertical: number, aabb: AABB2) {
  const absAngle = Math.abs(angleToVertical % (2 * Math.PI));
  const aabbWidth = aabb2Width(aabb);
  const aabbHeight = aabb2Height(aabb);
  const dims = { width: aabbWidth, height: aabbHeight };
  const flippedDims = { width: aabbHeight, height: aabbWidth };

  if (absAngle < Math.PI / 4) {
    return dims;
  }

  if (absAngle >= Math.PI / 4 && absAngle < (Math.PI * 3) / 4) {
    return flippedDims;
  }

  if (absAngle >= (Math.PI * 3) / 4 && absAngle < (Math.PI * 5) / 4) {
    return dims;
  }

  if (absAngle >= (Math.PI * 5) / 4 && absAngle < (Math.PI * 7) / 4) {
    return flippedDims;
  }

  return dims;
}

export function polyLine2LongestEdgeTangent(pl: PolyLine2) {
  let maxLength = 0;
  let maxLengthIndex;
  for (let i = 0; i < polyLine2EdgeCount(pl); i++) {
    const length = polyLine2EdgeLength(pl, i);
    if (length > maxLength) {
      maxLength = length;
      maxLengthIndex = i;
    }
  }
  return polyLine2EdgeTangent(pl, maxLengthIndex);
}

interface XsAndYs {
  x: number[];
  y: number[];
}

function polyLine2UniqueXsAndYs(pl: PolyLine2): XsAndYs {
  const xArr: number[] = [];
  const yArr: number[] = [];

  const edgeCount = pl.length / 2 - 1;

  for (let i = 0, l = pl.length; i < l; i += 2) {
    const [tx1, ty1] = polyLine2EdgeTangent(pl, (edgeCount - 1 + i / 2) % edgeCount);
    const [tx2, ty2] = polyLine2EdgeTangent(pl, i / 2);

    // don't store vertex xs and ys for collinear edges
    if (Math.abs(vec2AngleBetween(tx1, ty1, tx2, ty2)) < ANGLE_EPSILON) {
      continue;
    }

    const x = pl[i];
    const y = pl[i + 1];

    // first vertex, skip comparisons
    if (xArr.length === 0) {
      xArr.push(x);
      yArr.push(y);
      continue;
    }

    let xFoundMatch = false;
    for (const xCand of xArr) {
      if (Math.abs(x - xCand) < MAX_DIFFERENCE_TO_BE_DUPLICATE_NUMBER) {
        xFoundMatch = true;
        break;
      }
    }
    if (!xFoundMatch) {
      xArr.push(x);
    }

    let yFoundMatch = false;
    for (const yCand of yArr) {
      if (Math.abs(y - yCand) < MAX_DIFFERENCE_TO_BE_DUPLICATE_NUMBER) {
        yFoundMatch = true;
        break;
      }
    }
    if (!yFoundMatch) {
      yArr.push(y);
    }
  }

  // add intersections of axis aligned edges to diagonals to x and y arrays
  for (let i = 0, l = pl.length; i < l; i += 2) {
    const [x0, y0, x1, y1] = polyLine2Edge(pl, (edgeCount - 1 + i / 2) % edgeCount);
    if (line2IsAxisAligned(x0, y0, x1, y1)) {
      continue;
    }

    const [run, rise] = vec2Sub(x1, y1, x0, y0);
    const slope = rise / run;
    const minX = Math.min(x0, x1);
    const maxX = Math.max(x0, x1);

    for (const x of xArr) {
      if (x > minX && x < maxX) {
        const y = (x - x0) * slope + y0;
        let yFoundMatch = false;
        for (const yCand of yArr) {
          if (Math.abs(y - yCand) < MAX_DIFFERENCE_TO_BE_DUPLICATE_NUMBER) {
            yFoundMatch = true;
            break;
          }
        }
        if (!yFoundMatch) {
          yArr.push(y);
        }
      }
    }

    const minY = Math.min(y0, y1);
    const maxY = Math.max(y0, y1);
    for (const y of yArr) {
      if (y > minY && y < maxY) {
        const x = (y - y0) / slope + x0;
        let xFoundMatch = false;

        for (const xCand of xArr) {
          if (Math.abs(x - xCand) < MAX_DIFFERENCE_TO_BE_DUPLICATE_NUMBER) {
            xFoundMatch = true;
            break;
          }
        }

        if (!xFoundMatch) {
          xArr.push(x);
        }
      }
    }
  }

  return { x: xArr.sort(ascendingSortCompare), y: yArr.sort(ascendingSortCompare) };
}

interface ColRow {
  col: number;
  row: number;
}

/** adapted from original source https://gist.github.com/Aurelain/e471c0875a105b80db0e78daf6af4939, area of cells was added */
function maxRectangleWithArea(matrix: number[][]) {
  let column = 0; // iterator for columns
  let row = 0; // iterator for rows
  const columnCount = matrix[0].length; // number of columns;
  const rowCount = matrix.length; // number of rows
  const rowCache: number[] = [];
  const s: ColRow[] = []; // stack of {col, row} pairs
  let bestLl = { col: 0, row: 0 }; // lower-left corner
  let bestUr = { col: -1, row: -1 }; // upper-right corner
  let bestArea = 0;

  /** initialize a row cache with zero for each column cell */
  for (column = 0; column < columnCount + 1; ++column) {
    rowCache[column] = 0;
    s[column] = { col: 0, row: 0 };
  }

  /** step down each row */
  for (row = 0; row < rowCount; ++row) {
    /** populate a row cache with presence of column cells */
    for (column = 0; column < columnCount; ++column) {
      rowCache[column] = matrix[row][column] ? rowCache[column] + 1 : 0; // update cache
    }

    let openRowWidth = 0;

    /** step across each column */
    for (column = 0; column < columnCount + 1; ++column) {
      if (rowCache[column] > openRowWidth) {
        // open new rectangle
        s.push({ col: column, row: openRowWidth });
        openRowWidth = rowCache[column];
      } else if (rowCache[column] < openRowWidth) {
        // close rectangle
        let column0;
        let row0;
        let area;

        do {
          const cell = s.pop();
          if (cell === undefined) {
            break;
          }
          column0 = cell.col;
          row0 = cell.row;
          area = getAreaOfSubMatrix(matrix, column0, column, row - openRowWidth + 1, row + 1);
          if (area > bestArea) {
            bestArea = area;
            bestLl = { col: column0, row: row };
            bestUr = { col: column - 1, row: row - openRowWidth + 1 };
          }
          openRowWidth = row0;
        } while (rowCache[column] < openRowWidth);
        openRowWidth = rowCache[column];
        if (openRowWidth !== 0) {
          s.push({ col: column0, row: row0 });
        }
      }
    }
  }
  return {
    bestArea,
    lowerLeftColumn: bestLl.col,
    lowerLeftRow: bestLl.row,
    upperRightColumn: bestUr.col,
    upperRightRow: bestUr.row,
  };
}

function getAreaOfSubMatrix(
  matrix: number[][],
  columnStart: number,
  columnEnd: number,
  rowStart: number,
  rowEnd: number,
) {
  let area = 0;
  for (let row = rowStart; row < rowEnd; ++row) {
    /** populate a row cache with presence of column cells */
    for (let column = columnStart; column < columnEnd; ++column) {
      const cellArea = matrix[row][column];
      if (!(cellArea !== 0)) {
        console.assert(false);
      }
      area += cellArea;
    }
  }

  return area;
}

function polyLine2EncodeAsAreaArray(pl: PolyLine2, uniqueXs: number[], uniqueYs: number[]): number[][] {
  const polyLine2AreaArr: number[][] = [];
  for (let i = 0; i < uniqueYs.length - 1; i++) {
    const rowArr: number[] = [];
    const y = uniqueYs[i];
    const nextY = uniqueYs[i + 1];
    const height = nextY - y;

    for (let j = 0; j < uniqueXs.length - 1; j++) {
      const x = uniqueXs[j];
      const nextX = uniqueXs[j + 1];
      const width = nextX - x;
      const cellArea = width * height;

      let totalContainedTestPoints = 0;

      /** test 4 extreme corners of cell for containment in the polyline. if not contained, don't use cell */
      if (classifyContainmentVec2PolyLine2Fast(x + 1, y + 1, pl)) {
        totalContainedTestPoints++;
      }

      if (classifyContainmentVec2PolyLine2Fast(x + 1, y + height - 1, pl)) {
        totalContainedTestPoints++;
      }

      if (classifyContainmentVec2PolyLine2Fast(x + width - 1, y + 1, pl)) {
        totalContainedTestPoints++;
      }

      if (classifyContainmentVec2PolyLine2Fast(x + width - 1, y + height - 1, pl)) {
        totalContainedTestPoints++;
      }

      if (totalContainedTestPoints === 0) {
        rowArr.push(0);
      } else if (totalContainedTestPoints === 4) {
        rowArr.push(cellArea);
      } else {
        rowArr.push(0);
      }
    }
    polyLine2AreaArr.push(rowArr);
  }

  return polyLine2AreaArr;
}
